import { FunctionComponent, useState, useEffect, useRef } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
} from '@material-ui/core'
import { MapPin } from 'react-feather'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import { useParams, Link } from 'react-router-dom'
import colors from '../../colors'
import useMembershipsStore from '../../stores/memberships'
import useAuthStore from '../../stores/auth'

interface WalletConfig {
  id: number | null
  membershipId: number
  enabled: boolean
  backgroundColor: string | null
  foregroundColor: string | null
  labelColor: string | null
  stripImageUrl: string | null
  organizationLogoUrl: string | null
}

interface Location {
  id: number | null
  name: string
  latitude: number | null
  longitude: number | null
  address: string | null
  relevantText: string | null
}

interface ColorPickerInputProps {
  label: string
  value: string | null
  defaultValue: string
  onChange: (value: string) => void
  helperText: string
  error: boolean
}

const ColorPickerInput: FunctionComponent<ColorPickerInputProps> = ({
  label,
  value,
  defaultValue,
  onChange,
  helperText,
  error,
}) => {
  const colorInputRef = useRef<HTMLInputElement>(null)
  const styles = useColorPickerStyles()

  const openColorPicker = () => {
    colorInputRef.current?.click()
  }

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value.toUpperCase())
  }

  const currentColor = value || defaultValue

  return (
    <Box className={styles.colorInputContainer}>
      <Box className={styles.colorPickerRow} onClick={openColorPicker}>
        <Box
          className={styles.colorSwatch}
          style={{ backgroundColor: error ? '#CCCCCC' : currentColor }}
        />
        <Box className={styles.colorInputWrapper}>
          <TextField
            fullWidth
            label={label}
            value={value || ''}
            onChange={(e) => {
              e.stopPropagation()
              onChange(e.target.value.toUpperCase())
            }}
            onClick={(e) => e.stopPropagation()}
            placeholder={defaultValue}
            helperText={helperText}
            error={error}
          />
        </Box>
      </Box>
      <input
        ref={colorInputRef}
        type="color"
        value={currentColor}
        onChange={handleColorPickerChange}
        className={styles.hiddenColorInput}
      />
    </Box>
  )
}

const useColorPickerStyles = makeStyles({
  colorInputContainer: {
    position: 'relative' as const,
  },
  colorPickerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    cursor: 'pointer',
    padding: 8,
    marginLeft: -8,
    marginRight: -8,
    borderRadius: 8,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:hover $colorSwatch': {
      boxShadow: '0 0 0 3px rgba(63, 81, 181, 0.3)',
    },
  },
  colorSwatch: {
    width: 44,
    height: 44,
    borderRadius: 8,
    border: '2px solid #e0e0e0',
    flexShrink: 0,
    marginTop: 4,
    transition: 'box-shadow 0.15s ease',
  },
  colorInputWrapper: {
    flex: 1,
    minWidth: 0,
  },
  hiddenColorInput: {
    position: 'absolute' as const,
    opacity: 0,
    width: 0,
    height: 0,
    pointerEvents: 'none' as const,
  },
})

const WalletConfigView: FunctionComponent = () => {
  const { id } = useParams()
  const styles = useStyles()
  // Strip image upload commented out for now
  // const fileInputRef = useRef<HTMLInputElement>(null)
  const { memberships, getSimpleMembership } = useMembershipsStore()
  const organizationName = useAuthStore((state) => state.user?.organization?.name)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showBack, setShowBack] = useState(false)
  // Strip image upload commented out for now
  // const [stripImageFile, setStripImageFile] = useState<File | null>(null)
  // const [stripImagePreview, setStripImagePreview] = useState<string | null>(null)
  const [benefits, setBenefits] = useState<any[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [config, setConfig] = useState<WalletConfig>({
    id: null,
    membershipId: 0,
    enabled: false,
    backgroundColor: '#000000',
    foregroundColor: '#FFFFFF',
    labelColor: '#CCCCCC',
    stripImageUrl: null,
    organizationLogoUrl: null,
  })

  useEffect(() => {
    if (!id) return
    getSimpleMembership(id)

    const fetchConfig = async () => {
      try {
        const resp = await axios.get(`/memberships/${id}/wallet_config`)
        setConfig({
          id: resp.data.id,
          membershipId: resp.data.membershipId,
          enabled: resp.data.enabled || false,
          backgroundColor: resp.data.backgroundColor || '#000000',
          foregroundColor: resp.data.foregroundColor || '#FFFFFF',
          labelColor: resp.data.labelColor || '#CCCCCC',
          stripImageUrl: resp.data.stripImageUrl,
          organizationLogoUrl: resp.data.organizationLogoUrl,
        })
      } catch (error) {
        toast.error('Failed to load wallet configuration')
      } finally {
        setLoading(false)
      }
    }

    fetchConfig()
  }, [id])

  useEffect(() => {
    if (!id) return
    const fetchBenefits = async () => {
      try {
        const resp = await axios.get(`/memberships/${id}/benefits`)
        setBenefits(resp.data || [])
      } catch (error) {
        // best-effort; no toast to avoid noise
      }
    }
    fetchBenefits()
  }, [id])

  useEffect(() => {
    if (!id) return
    const fetchLocations = async () => {
      try {
        const resp = await axios.get('/locations')
        setLocations(resp.data || [])
      } catch (error) {
        // best-effort; no toast to avoid noise
      }
    }
    fetchLocations()
  }, [id])

  // Onboarding tour for first-time visitors
  const TOUR_KEY = 'wallet_config_tour_completed'
  useEffect(() => {
    if (loading) return
    if (localStorage.getItem(TOUR_KEY)) return

    // Small delay to ensure DOM elements are rendered
    const timer = setTimeout(() => {
      const tourDriver = driver({
        showProgress: true,
        allowClose: true,
        doneBtnText: 'Done',
        nextBtnText: 'Next',
        prevBtnText: 'Previous',
        steps: [
          {
            element: '[data-tour="wallet-enable"]',
            popover: {
              title: 'Enable Apple Wallet',
              description: 'Turn this on to activate Apple Wallet passes for this membership.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '[data-tour="wallet-colors"]',
            popover: {
              title: 'Customize Colors',
              description: 'Match your card\'s colors to your brand. Changes appear in the preview instantly.',
              side: 'bottom',
              align: 'start',
            },
          },
          {
            element: '[data-tour="wallet-preview"]',
            popover: {
              title: 'Live Preview',
              description: 'See exactly how your card will look on a member\'s phone.',
              side: 'left',
              align: 'start',
            },
          },
          {
            element: '[data-tour="wallet-locations"]',
            popover: {
              title: 'Location Notifications',
              description: 'Your business locations make the card appear on members\' lock screens when nearby. Manage them in Settings > Locations.',
              side: 'top',
              align: 'start',
            },
          },
          {
            element: '[data-tour="wallet-save"]',
            popover: {
              title: 'Don\'t Forget to Save!',
              description: 'Important: Always click Save after making changes to update the configuration.',
              side: 'top',
              align: 'start',
            },
          },
        ],
        onDestroyStarted: () => {
          localStorage.setItem(TOUR_KEY, 'true')
          tourDriver.destroy()
        },
      })

      tourDriver.drive()
    }, 500)

    return () => clearTimeout(timer)
  }, [loading])

  // Strip image upload handlers commented out for now
  // const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0]
  //   if (!file) return
  //
  //   // Validate file type
  //   if (file.type !== 'image/png') {
  //     toast.error('Please select a PNG file')
  //     return
  //   }
  //
  //   // Validate file size (2MB max)
  //   if (file.size > 2 * 1024 * 1024) {
  //     toast.error('File size must be less than 2MB')
  //     return
  //   }
  //
  //   setStripImageFile(file)
  //   setStripImagePreview(URL.createObjectURL(file))
  // }
  //
  // const handleChangeImage = () => {
  //   // Clear pending selection and trigger file picker
  //   setStripImageFile(null)
  //   setStripImagePreview(null)
  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = ''
  //     fileInputRef.current.click()
  //   }
  // }

  const handleSave = async () => {
    if (!id) return

    setSaving(true)
    try {
      const formData = new FormData()
      formData.append('wallet_config[enabled]', String(config.enabled))
      if (config.backgroundColor) {
        formData.append('wallet_config[background_color]', config.backgroundColor)
      }
      if (config.foregroundColor) {
        formData.append('wallet_config[foreground_color]', config.foregroundColor)
      }
      if (config.labelColor) {
        formData.append('wallet_config[label_color]', config.labelColor)
      }
      // Strip image upload commented out for now
      // if (stripImageFile) {
      //   formData.append('wallet_config[strip_image]', stripImageFile)
      // }

      const resp = await axios.put(`/memberships/${id}/wallet_config`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      setConfig({
        id: resp.data.id,
        membershipId: resp.data.membershipId,
        enabled: resp.data.enabled,
        backgroundColor: resp.data.backgroundColor,
        foregroundColor: resp.data.foregroundColor,
        labelColor: resp.data.labelColor,
        stripImageUrl: resp.data.stripImageUrl,
        organizationLogoUrl: resp.data.organizationLogoUrl,
      })
      // Strip image clearing commented out for now
      // setStripImageFile(null)
      // setStripImagePreview(null)
      toast.success('Wallet configuration saved')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to save wallet configuration'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const isValidHexColor = (color: string | null): boolean => {
    if (!color) return true
    return /^#[0-9A-Fa-f]{6}$/.test(color)
  }

  const hasValidationErrors = (): boolean => {
    return !isValidHexColor(config.backgroundColor) ||
      !isValidHexColor(config.foregroundColor) ||
      !isValidHexColor(config.labelColor)
  }

  // Strip image preview commented out for now
  // const displayImageUrl = stripImagePreview || config.stripImageUrl

  const previewBackground =
    isValidHexColor(config.backgroundColor) && config.backgroundColor ? config.backgroundColor : '#000000'
  const previewForeground =
    isValidHexColor(config.foregroundColor) && config.foregroundColor ? config.foregroundColor : '#FFFFFF'
  const previewLabel =
    isValidHexColor(config.labelColor) && config.labelColor ? config.labelColor : '#CCCCCC'
  const accent = previewLabel || '#FACC15'
  const membership = id ? memberships[id] : undefined
  const featureRows = benefits.slice(0, 4)

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box p={4} className={styles.container}>
      <Box mb={4}>
        <Typography variant="h3">Apple Wallet Configuration</Typography>
        <Typography variant="body2" color="textSecondary">
          Configure the appearance of the Apple Wallet pass for this membership.
        </Typography>
      </Box>

      <Box className={styles.contentGrid}>
        <Box className={styles.formContainer}>
          <Box mb={3} data-tour="wallet-enable">
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => {
                    const next = e.target.checked
                    if (next && hasValidationErrors()) {
                      toast.error('Please enter valid colors before enabling Wallet')
                      return
                    }
                    setConfig({ ...config, enabled: next })
                  }}
                  color="primary"
                />
              }
              label="Enable Apple Wallet"
            />
            <Typography variant="caption" color="textSecondary" display="block">
              When enabled, patrons can add this membership to their Apple Wallet.
            </Typography>
          </Box>

          <Box mb={3} data-tour="wallet-colors">
            <ColorPickerInput
              label="Background Color"
              value={config.backgroundColor}
              defaultValue="#000000"
              onChange={(value) => setConfig({ ...config, backgroundColor: value })}
              helperText="Click to pick a color, or enter hex code"
              error={!isValidHexColor(config.backgroundColor)}
            />
          </Box>

          <Box mb={3}>
            <ColorPickerInput
              label="Foreground Color"
              value={config.foregroundColor}
              defaultValue="#FFFFFF"
              onChange={(value) => setConfig({ ...config, foregroundColor: value })}
              helperText="Text color on the pass"
              error={!isValidHexColor(config.foregroundColor)}
            />
          </Box>

          <Box mb={3}>
            <ColorPickerInput
              label="Label Color"
              value={config.labelColor}
              defaultValue="#CCCCCC"
              onChange={(value) => setConfig({ ...config, labelColor: value })}
              helperText="Color for field labels"
              error={!isValidHexColor(config.labelColor)}
            />
          </Box>

          {/* Strip image upload commented out for now
          <Box mb={3} p={2} className={styles.stripImageContainer}>
            <Typography variant="subtitle2">Strip Image</Typography>
            <Typography variant="body2" color="textSecondary">
              The horizontal banner (1125×432px PNG, max 2MB) shown at the top of the wallet pass.
            </Typography>

            {displayImageUrl ? (
              <Box mt={2}>
                <img
                  src={displayImageUrl}
                  alt="Strip preview"
                  className={styles.stripImagePreview}
                />
                <Box mt={1}>
                  {stripImagePreview && (
                    <Typography variant="caption" color="textSecondary">
                      New image selected - click Save to upload
                    </Typography>
                  )}
                  <Button
                    size="small"
                    onClick={handleChangeImage}
                    style={{ marginTop: 8, display: 'block', backgroundColor: '#fbbf24', color: '#111827' }}
                  >
                    Change Image
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ borderColor: '#fbbf24', color: '#b45309' }}
                >
                  Select Image
                </Button>
              </Box>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>
          */}

          <Box mt={4} data-tour="wallet-save">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || hasValidationErrors()}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </Box>
        </Box>

        <Box className={styles.previewContainer} data-tour="wallet-preview">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">
              Live Preview
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowBack(!showBack)}
              style={{ textTransform: 'none' }}
            >
              {showBack ? '← See front' : 'See back →'}
            </Button>
          </Box>
          <Box
            className={styles.passPreview}
            style={{
              backgroundColor: previewBackground,
              color: previewForeground,
              opacity: config.enabled ? 1 : 0.55,
            }}
          >
            {!showBack ? (
              <>
                {/* FRONT OF PASS */}
                {/* Header row: Logo + Org name | CODE */}
                <Box className={styles.passHeader}>
                  <Box className={styles.passHeaderLeft}>
                    {config.organizationLogoUrl ? (
                      <img
                        src={config.organizationLogoUrl}
                        alt="Logo"
                        className={styles.passLogo}
                      />
                    ) : (
                      <Box className={styles.passLogoPlaceholder} />
                    )}
                    <Typography style={{ color: previewLabel, fontSize: 18, fontWeight: 600 }}>
                      {organizationName || 'Organization'}
                    </Typography>
                  </Box>
                  <Box className={styles.passHeaderRight}>
                    <Typography style={{ color: previewLabel, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase' }}>
                      CODE
                    </Typography>
                    <Typography style={{ color: previewForeground, fontWeight: 700, fontSize: 22 }}>
                      A7K2
                    </Typography>
                  </Box>
                </Box>

                {/* Primary field: Membership title */}
                <Box className={styles.passBody} style={{ backgroundColor: previewBackground }}>
                  <Typography style={{ color: previewForeground, fontSize: 32, fontWeight: 400, lineHeight: 1.2 }}>
                    {membership?.title || 'Membership Name'}
                  </Typography>

                  {/* Status field */}
                  <Box style={{ marginTop: 24 }}>
                    <Typography style={{ color: previewLabel, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      STATUS
                    </Typography>
                    <Typography style={{ color: previewForeground, fontSize: 20, fontWeight: 400 }}>
                      Active
                    </Typography>
                  </Box>

                  {/* Benefits as secondary fields */}
                  <Box className={styles.passBenefitsRow}>
                    {featureRows.length ? featureRows.slice(0, 2).map((benefit) => (
                      <Box key={benefit.id} className={styles.passBenefitField}>
                        <Typography style={{ color: previewLabel, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }} noWrap>
                          {benefit.title}
                        </Typography>
                        <Typography style={{ color: previewForeground, fontSize: 18, fontWeight: 400 }}>
                          {benefit.interval === 'unlimited' ? 'Unlimited' : `0 of ${benefit.quantity || 1} left`}
                        </Typography>
                      </Box>
                    )) : (
                      <>
                        <Box className={styles.passBenefitField}>
                          <Typography style={{ color: previewLabel, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                            BENEFIT ONE
                          </Typography>
                          <Typography style={{ color: previewForeground, fontSize: 18, fontWeight: 400 }}>
                            0/4
                          </Typography>
                        </Box>
                        <Box className={styles.passBenefitField}>
                          <Typography style={{ color: previewLabel, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                            BENEFIT TWO
                          </Typography>
                          <Typography style={{ color: previewForeground, fontSize: 18, fontWeight: 400 }}>
                            ∞
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </>
            ) : (
              <>
                {/* BACK OF PASS */}
                <Box className={styles.passBackContent}>
                  {/* VIP Link */}
                  <Box className={styles.passBackField}>
                    <Typography style={{ color: previewLabel, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      MANAGE MEMBERSHIP
                    </Typography>
                    <Typography style={{ color: '#007AFF', fontSize: 14, textDecoration: 'underline' }}>
                      my.stitch.vip
                    </Typography>
                  </Box>

                  {/* Member Name */}
                  <Box className={styles.passBackField}>
                    <Typography style={{ color: previewLabel, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      MEMBER
                    </Typography>
                    <Typography style={{ color: previewForeground, fontSize: 14 }}>
                      Jane Smith
                    </Typography>
                  </Box>

                  {/* Email */}
                  <Box className={styles.passBackField}>
                    <Typography style={{ color: previewLabel, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      EMAIL
                    </Typography>
                    <Typography style={{ color: previewForeground, fontSize: 14 }}>
                      jane.smith@example.com
                    </Typography>
                  </Box>

                  {/* Member Since */}
                  <Box className={styles.passBackField}>
                    <Typography style={{ color: previewLabel, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      MEMBER SINCE
                    </Typography>
                    <Typography style={{ color: previewForeground, fontSize: 14 }}>
                      March 2025
                    </Typography>
                  </Box>

                  {/* Overflow Benefits (if more than 2) */}
                  {featureRows.length > 2 && featureRows.slice(2).map((benefit) => (
                    <Box key={benefit.id} className={styles.passBackField}>
                      <Typography style={{ color: previewLabel, fontSize: 11, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                        {benefit.title}
                      </Typography>
                      <Typography style={{ color: previewForeground, fontSize: 14 }}>
                        {benefit.interval === 'unlimited' ? 'Unlimited' : `0 of ${benefit.quantity || 1} left`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
          {!config.enabled && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
              Enable Apple Wallet to match the live pass state.
            </Typography>
          )}

          {/* Location-Based Notifications summary (managed in Settings > Locations) */}
          <Box mt={4} className={styles.locationsSection} data-tour="wallet-locations">
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <MapPin size={20} />
              <Typography variant="subtitle1">
                {locations.length > 0
                  ? `Location-based notifications at ${locations.length} ${locations.length === 1 ? 'location' : 'locations'}`
                  : 'Location-based notifications'}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              {locations.length > 0
                ? "When members are near one of your locations, this pass appears on their lock screen."
                : "Add business locations so this pass appears on members' lock screens when they're nearby."}
            </Typography>
            <Box mt={1}>
              <Link to="/settings/locations" className={styles.locationsLink}>
                Manage locations in Settings →
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 24,
    alignItems: 'start',
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 420px',
    },
  },
  formContainer: {
    width: '100%',
    maxWidth: 500,
  },
  previewContainer: {
    width: '100%',
  },
  stripImageContainer: {
    border: '1px dashed #e0e0e0',
    borderRadius: 4,
    backgroundColor: colors.GREY_7,
  },
  stripImagePreview: {
    maxWidth: '100%',
    borderRadius: 4,
  },
  passPreview: {
    borderRadius: 16,
    overflow: 'hidden' as const,
    boxShadow: '0 18px 40px rgba(0,0,0,0.20)',
    position: 'relative' as const,
    border: '1px solid rgba(255,255,255,0.1)',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, \"SF Pro Text\", \"Segoe UI\", Roboto, Arial, sans-serif',
    minHeight: 340,
    width: '100%',
  },
  passHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '16px 20px 12px',
  },
  passHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  passHeaderRight: {
    textAlign: 'right' as const,
  },
  passLogo: {
    width: 40,
    height: 40,
    borderRadius: 8,
    objectFit: 'cover' as const,
  },
  passLogoPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  passBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '8px 20px 28px',
  },
  passBenefitsRow: {
    display: 'flex',
    gap: 24,
    marginTop: 16,
  },
  passBenefitField: {
    flex: 1,
    minWidth: 0,
  },
  passBackContent: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
    minHeight: 300,
  },
  passBackField: {
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    paddingBottom: 12,
  },
  passHero: {
    position: 'relative',
    padding: 0,
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    overflow: 'hidden' as const,
  },
  passStrip: {
    height: 190,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.10)',
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    overflow: 'hidden' as const,
  },
  passHeroRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
  },
  passCode: {
    textAlign: 'right' as const,
  },
  passDivider: {
    borderTopWidth: 1,
    borderTopStyle: 'solid' as const,
    opacity: 0.8,
    margin: '6px 0 4px',
  },
  passFeatureRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
  },
  passQr: {
    display: 'flex',
    justifyContent: 'center',
    padding: '12px 0 6px',
  },
  passQrInner: {
    width: 160,
    height: 52,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.18)',
  },
  locationsSection: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 16,
    backgroundColor: '#fafafa',
  },
  locationsLink: {
    color: colors.STITCH_BLUE,
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
})

export default WalletConfigView
